import { FC, useCallback, useReducer, useState } from 'react';
import { Button } from '@mui/material';
import { toast } from 'react-toastify';
import Dialog from 'components/Dialog';
import EnrolPinDialog from 'components/PinPad/EnrolPinDialog';
import PinLoginDialog from 'components/PinPad/PinLoginDialog';
import { useLanguage } from 'context/AppContext';
import * as Styles from './SettingsModal.styles';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import { useQuickSwitchUsers } from 'api/hooks/useQuickSwitchUsers';
import { subscriptionTiers } from 'api/models/Users';
import { useAuth0User } from 'hooks/useAuth0User';
import { useAuthorization } from 'hooks/useAuth';
import { useDeletePin } from 'hooks/useDeletePin';
import { usePinLoginEnabled } from 'hooks/usePinLoginEnabled';
import { usePinLockout } from 'hooks/usePinLockout';
import { usePinOrgId } from 'hooks/usePinOrgId';
import { formatClockTime } from 'utils/helpers/time.helpers';
import { hasCachedPinKey, isCachedRtExpired } from 'utils/helpers/pinKeyCache.helpers';
import { getUserRoles } from 'utils/helpers/permissions.helpers';
import UserQuickSwitcher from 'components/UserQuickSwitcher/UserQuickSwitcher';
import LogoutButton from 'components/LogoutButton';
import theme from 'App.theme';

interface SettingsModalProps {
  open: boolean;
  close: VoidFunction;
  isAuthenticated: boolean;
}

const SettingsModal: FC<SettingsModalProps> = ({ open, close, isAuthenticated }) => {
  const [language] = useLanguage();

  const { data: user } = useCurrentUser();
  const auth0User = useAuth0User();
  const { login } = useAuthorization();
  const { quickSwitchUsers } = useQuickSwitchUsers();
  const pinEnabled = usePinLoginEnabled();
  const orgId = usePinOrgId();
  const { deletePin } = useDeletePin();

  const [pinLoginEmail, setPinLoginEmail] = useState<string | null>(null);
  const [enrolOpen, setEnrolOpen] = useState(false);
  const [removingPin, setRemovingPin] = useState(false);
  const [, refreshPinState] = useReducer((n: number) => n + 1, 0);
  const pinLang = language.PinLogin;
  const hasPin = !!user?.email && hasCachedPinKey(orgId, user.email);
  const [lockedUntil, refreshLockout] = usePinLockout(orgId, user?.email);

  const userId = (isAuthenticated && user?.email) || 'Guest';
  const tenantName = (isAuthenticated && user?.tenant.description) || '';
  const userRoles = getUserRoles(auth0User);
  const userRolesStr = userRoles.join(', ');
  const subscriptionId = isAuthenticated ? user?.tenant?.subscriptionId || 't1' : '';
  const subscription = subscriptionId && subscriptionTiers[subscriptionId];

  const switchToUser = useCallback(
    (email: string | null) => {
      if (!email || email == user?.email) return;

      // a PIN is only offered while its refresh token can still be redeemed
      if (pinEnabled && orgId && hasCachedPinKey(orgId, email) && !isCachedRtExpired(orgId, email)) {
        setPinLoginEmail(email);
        return;
      }

      close();
      login(true, undefined, email);
    },
    [close, login, user?.email, pinEnabled, orgId],
  );

  const removePin = useCallback(async () => {
    setRemovingPin(true);
    try {
      await deletePin();
      toast.success(pinLang.manage.removed);
      refreshPinState();
    } catch {
      toast.error(pinLang.manage.removeError);
    } finally {
      setRemovingPin(false);
    }
  }, [deletePin, pinLang]);

  return (
    <>
      <Dialog
        open={open}
        onClose={close}
        onConfirm={close}
        title={language.SettingsModal.userDetails}
        onEnterKey="confirm"
        confirmLabel={language.Dialog.Close}
        body={
          <Styles.Container>
            <div>
              <Styles.Title>{language.SettingsModal.user}</Styles.Title>
              <Styles.Description>{userId}</Styles.Description>
            </div>
            {userRolesStr && (
              <div>
                <Styles.Title>
                  {userRoles.length == 1 ? language.SettingsModal.role : language.SettingsModal.roles}
                </Styles.Title>
                <Styles.Description>{userRolesStr}</Styles.Description>
              </div>
            )}
            {tenantName && (
              <div>
                <Styles.Title>{language.SettingsModal.tenant}</Styles.Title>
                <Styles.Description>{tenantName}</Styles.Description>
              </div>
            )}
            {subscription && (
              <div>
                <Styles.Title>{language.SettingsModal.subscription}</Styles.Title>
                <Styles.Description>{subscription}</Styles.Description>
              </div>
            )}
            {isAuthenticated && quickSwitchUsers.length > 1 && (
              <div>
                <Styles.Title>{language.SettingsModal.switchUser}</Styles.Title>
                <UserQuickSwitcher
                  value={quickSwitchUsers.find(({ email }) => email == user?.email) ?? null}
                  onChange={option => switchToUser(option?.email ?? null)}
                  options={quickSwitchUsers}
                />
              </div>
            )}
            {isAuthenticated && (
              <div>
                <Button
                  onClick={() => {
                    close();
                    login(true, undefined, null);
                  }}
                >
                  {language.SettingsModal.differentAccount}
                </Button>
              </div>
            )}
            {isAuthenticated && pinEnabled && (
              <div>
                <Styles.Title>{pinLang.manage.heading}</Styles.Title>
                <Styles.Description>{hasPin ? pinLang.manage.isSet : pinLang.manage.notSet}</Styles.Description>
                <Button onClick={() => setEnrolOpen(true)} disabled={!!lockedUntil || removingPin}>
                  {hasPin ? pinLang.manage.change : pinLang.manage.setUp}
                </Button>
                {hasPin && (
                  <Button onClick={removePin} disabled={removingPin}>
                    {pinLang.manage.remove}
                  </Button>
                )}
                {lockedUntil && (
                  <Styles.Description>
                    {pinLang.manage.lockedUntil({ time: formatClockTime(lockedUntil) })}
                  </Styles.Description>
                )}
              </div>
            )}
            <div style={{ textAlign: 'left', marginTop: theme.layout.spacing.medium, width: '48px' }}>
              <LogoutButton />
            </div>
          </Styles.Container>
        }
      />
      {pinLoginEmail && (
        <PinLoginDialog
          open
          email={pinLoginEmail}
          onClose={() => setPinLoginEmail(null)}
          onUsePassword={() => {
            const email = pinLoginEmail;
            setPinLoginEmail(null);
            close();
            login(true, undefined, email);
          }}
        />
      )}
      <EnrolPinDialog
        open={enrolOpen}
        isChange={hasPin}
        onClose={() => {
          setEnrolOpen(false);
          refreshPinState();
          refreshLockout();
        }}
      />
    </>
  );
};

export default SettingsModal;
